package com.example.positionahead.domain

import com.example.positionahead.model.DiscoverAssetClass
import com.example.positionahead.model.DiscoverIdea
import com.example.positionahead.model.DiscoverSection
import com.example.positionahead.model.DiscoverSectionKind
import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.Quote
import com.example.positionahead.model.SymbolPerformance
import com.example.positionahead.model.plannerGoalLabel
import com.example.positionahead.model.objectiveLabel

object DiscoverAnalyzer {

    const val DISCLAIMER =
        "Research ideas only — you decide what to buy. Not financial advice."

    fun analyze(
        ownedSymbols: Set<String>,
        goal: FinancialGoal,
        quotes: Map<String, Quote>,
        performance: Map<String, SymbolPerformance> = emptyMap(),
    ): List<DiscoverSection> = buildList {
        forYourGoal(ownedSymbols, goal, quotes, performance)?.let(::add)
        byAssetClass(
            kind = DiscoverSectionKind.STOCKS,
            title = "Stocks",
            subtitle = "Individual companies you can buy and sell like any broker",
            assetClass = DiscoverAssetClass.STOCK,
            ownedSymbols = ownedSymbols,
            goal = goal,
            quotes = quotes,
            performance = performance,
        )?.let(::add)
        byAssetClass(
            kind = DiscoverSectionKind.ETFS,
            title = "ETFs",
            subtitle = "Exchange-traded funds — diversified, low-cost, liquid",
            assetClass = DiscoverAssetClass.ETF,
            ownedSymbols = ownedSymbols,
            goal = goal,
            quotes = quotes,
            performance = performance,
        )?.let(::add)
        byAssetClass(
            kind = DiscoverSectionKind.BONDS,
            title = "Bonds & fixed income",
            subtitle = "Bond ETFs for stability, income, and downside protection",
            assetClass = DiscoverAssetClass.BOND,
            ownedSymbols = ownedSymbols,
            goal = goal,
            quotes = quotes,
            performance = performance,
        )?.let(::add)
        byAssetClass(
            kind = DiscoverSectionKind.MUTUAL_FUNDS,
            title = "Mutual funds",
            subtitle = "Index and income funds available at major brokerages",
            assetClass = DiscoverAssetClass.MUTUAL_FUND,
            ownedSymbols = ownedSymbols,
            goal = goal,
            quotes = quotes,
            performance = performance,
        )?.let(::add)
        incomeOpportunities(ownedSymbols, goal, quotes, performance)?.let(::add)
    }

    private fun forYourGoal(
        ownedSymbols: Set<String>,
        goal: FinancialGoal,
        quotes: Map<String, Quote>,
        performance: Map<String, SymbolPerformance>,
    ): DiscoverSection? {
        val owned = ownedSymbols.map { it.uppercase() }.toSet()
        val ideas = MarketSuggestionCatalog.candidatesFor(goal)
            .filter { entry ->
                entry.symbol.uppercase() !in owned && quotes.containsKey(entry.symbol.uppercase())
            }
            .take(6)
            .mapNotNull { entry ->
                ideaFromEntry(entry, quotes, performance, personalizedRationale(entry, goal))
            }
        if (ideas.isEmpty()) return null
        return DiscoverSection(
            kind = DiscoverSectionKind.FOR_YOUR_GOAL,
            title = "For your ${goal.plannerGoalLabel()} goal",
            subtitle = "Ideas aligned with your ${goal.objectiveLabel()} strategy — build your own portfolio, on your terms",
            ideas = ideas,
        )
    }

    private fun byAssetClass(
        kind: DiscoverSectionKind,
        title: String,
        subtitle: String,
        assetClass: DiscoverAssetClass,
        ownedSymbols: Set<String>,
        goal: FinancialGoal,
        quotes: Map<String, Quote>,
        performance: Map<String, SymbolPerformance>,
    ): DiscoverSection? {
        val owned = ownedSymbols.map { it.uppercase() }.toSet()
        val ideas = MarketSuggestionCatalog.forAssetClass(assetClass)
            .filter { entry ->
                goal.objective in entry.objectives &&
                    entry.symbol.uppercase() !in owned &&
                    quotes.containsKey(entry.symbol.uppercase())
            }
            .take(5)
            .mapNotNull { entry ->
                ideaFromEntry(entry, quotes, performance, entry.rationale)
            }
        if (ideas.isEmpty()) return null
        return DiscoverSection(kind = kind, title = title, subtitle = subtitle, ideas = ideas)
    }

    private fun incomeOpportunities(
        ownedSymbols: Set<String>,
        goal: FinancialGoal,
        quotes: Map<String, Quote>,
        performance: Map<String, SymbolPerformance>,
    ): DiscoverSection? {
        if (goal.objective != com.example.positionahead.model.PortfolioObjective.INCOME &&
            goal.plannerGoalType != com.example.positionahead.model.PlannerGoalType.FINANCIAL_INDEPENDENCE &&
            goal.plannerGoalType != com.example.positionahead.model.PlannerGoalType.RETIREMENT
        ) {
            return null
        }
        val owned = ownedSymbols.map { it.uppercase() }.toSet()
        val ideas = MarketSuggestionCatalog.incomeCandidates()
            .filter { it.symbol.uppercase() !in owned }
            .mapNotNull { entry ->
                ideaFromEntry(entry, quotes, performance, entry.rationale)
            }
            .sortedByDescending { it.dividendYield }
            .take(5)
        if (ideas.isEmpty()) return null
        return DiscoverSection(
            kind = DiscoverSectionKind.INCOME_OPPORTUNITIES,
            title = "Income opportunities",
            subtitle = "Yield-focused ideas to grow cash flow without paying an advisor",
            ideas = ideas,
        )
    }

    private fun personalizedRationale(entry: CatalogEntry, goal: FinancialGoal): String =
        "${entry.rationale} — fits your ${goal.plannerGoalLabel()} target of ${formatMoney(goal.targetAmount)}"

    private fun ideaFromEntry(
        entry: CatalogEntry,
        quotes: Map<String, Quote>,
        performance: Map<String, SymbolPerformance>,
        whyConsider: String,
    ): DiscoverIdea? {
        val upper = entry.symbol.uppercase()
        val quote = quotes[upper] ?: return null
        val perf = performance[upper]
        return DiscoverIdea(
            symbol = upper,
            name = quote.name.ifBlank { entry.name },
            assetClass = entry.assetClass,
            price = quote.price,
            changePercent = quote.changePercent,
            dividendYield = quote.dividendYield,
            whyConsider = whyConsider,
            return1MonthPercent = perf?.return1MonthPercent,
            returnYtdPercent = perf?.returnYtdPercent,
            yearHigh = quote.yearHigh,
            yearLow = quote.yearLow,
            peRatio = quote.peRatio,
            researchSummary = buildResearchSummary(quote, perf),
        )
    }

    private fun buildResearchSummary(quote: Quote, perf: SymbolPerformance?): String = buildString {
        perf?.return1MonthPercent?.let { append("1M ${it.format(1)}% · ") }
        perf?.returnYtdPercent?.let { append("YTD ${it.format(1)}% · ") }
        if (quote.dividendYield > 0) append("Yield ${quote.dividendYield.format(2)}% · ")
        if (quote.yearHigh != null && quote.yearLow != null) {
            append("52-wk ${quote.yearLow.format(0)}–${quote.yearHigh.format(0)}")
        }
        quote.peRatio?.takeIf { it > 0 }?.let {
            if (isNotEmpty()) append(" · ")
            append("P/E ${it.format(1)}")
        }
    }.trim().removeSuffix("·").trim()

    private fun formatMoney(value: Double): String = when {
        value >= 1_000_000 -> "$${(value / 1_000_000).format(1)}M"
        value >= 1_000 -> "$${(value / 1_000).format(0)}K"
        else -> "$${value.format(0)}"
    }

    private fun Double.format(decimals: Int): String {
        val factor = 10.0.pow(decimals)
        val rounded = kotlin.math.round(this * factor) / factor
        return if (decimals == 0) rounded.toInt().toString() else rounded.toString()
    }

    private fun Double.pow(n: Int): Double {
        var result = 1.0
        repeat(n) { result *= 10.0 }
        return result
    }
}
